const brainGameLevels = require('./brainGameLevels');

const { BRAIN_GAME_LEVEL_CONFIG } = brainGameLevels;

const GAME_SCORE_HISTORY_LIMIT = 30;
const SOPT_PROGRESS_GAME_IDS = Object.freeze(['sopt-classic', 'sopt-advanced']);
const VALID_GAME_IDS = new Set([
    ...SOPT_PROGRESS_GAME_IDS,
    ...Object.keys(BRAIN_GAME_LEVEL_CONFIG),
]);
const VALID_METRICS = new Set(['score', 'streak', 'elapsedMs', 'level']);

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const normalizeRecordedAt = (value) => {
    if (!value) {
        return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    return Number.isFinite(date.getTime()) ? date.toISOString() : '';
};

const normalizeOptionalPositiveNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? number : null;
};

const normalizeGameScorePoint = (rawPoint = {}) => {
    const metric = `${rawPoint?.metric || ''}`.trim();
    const rawValue = Number(rawPoint?.value);

    if (!VALID_METRICS.has(metric) || !Number.isFinite(rawValue)) {
        return null;
    }

    if (metric === 'elapsedMs' && rawValue <= 0) {
        return null;
    }

    const maxValue = normalizeOptionalPositiveNumber(rawPoint.maxValue);
    const durationMs = normalizeOptionalPositiveNumber(rawPoint.durationMs);
    const rawLevel = Number(rawPoint.level);
    const point = {
        metric,
        value: metric === 'elapsedMs' ? Math.max(1, Math.round(rawValue)) : Math.round(rawValue),
        recordedAt: normalizeRecordedAt(
            rawPoint.recordedAt || rawPoint.achievedAt || rawPoint.at
        ),
    };

    if (maxValue !== null) {
        point.maxValue = maxValue;
    }

    if (durationMs !== null) {
        point.durationMs = Math.round(durationMs);
    }

    if (Number.isFinite(rawLevel)) {
        point.level = Math.max(0, Math.round(rawLevel));
    }

    return point;
};

const normalizeGameScoreSeries = (rawSeries = []) => {
    const points = (Array.isArray(rawSeries) ? rawSeries : [])
        .map(normalizeGameScorePoint)
        .filter(Boolean);

    if (!points.length) {
        return [];
    }

    const currentMetric = points[points.length - 1].metric;

    return points
        .filter((point) => point.metric === currentMetric)
        .slice(-GAME_SCORE_HISTORY_LIMIT);
};

const createGameScoreHistory = (rawHistory = {}) => Object.fromEntries(
    [...VALID_GAME_IDS]
        .map((gameId) => [gameId, normalizeGameScoreSeries(rawHistory?.[gameId])])
        .filter(([, points]) => points.length > 0)
);

const recordGameScore = (
    rawHistory,
    gameId,
    rawPoint,
    recordedAt = new Date()
) => {
    const normalizedGameId = `${gameId || ''}`.trim();
    const history = createGameScoreHistory(rawHistory);

    if (!VALID_GAME_IDS.has(normalizedGameId)) {
        return history;
    }

    const point = normalizeGameScorePoint({
        ...rawPoint,
        recordedAt: rawPoint?.recordedAt || rawPoint?.achievedAt || rawPoint?.at || recordedAt,
    });

    if (!point) {
        return history;
    }

    const existing = history[normalizedGameId] || [];
    const comparable = existing.filter((entry) => entry.metric === point.metric);
    const previous = comparable[comparable.length - 1];

    if (
        previous
        && previous.value === point.value
        && previous.recordedAt
        && previous.recordedAt === point.recordedAt
    ) {
        return history;
    }

    return {
        ...history,
        [normalizedGameId]: [...comparable, point].slice(-GAME_SCORE_HISTORY_LIMIT),
    };
};

const seedGameScoreHistory = (rawHistory, seeds = {}) => {
    let history = createGameScoreHistory(rawHistory);

    Object.entries(seeds || {}).forEach(([gameId, rawPoint]) => {
        if ((history[gameId] || []).length > 0) {
            return;
        }

        history = recordGameScore(history, gameId, rawPoint, rawPoint?.recordedAt || new Date());
    });

    return history;
};

const isLowerGameScoreBetter = (metric) => metric === 'elapsedMs';

const getBestGameScorePoint = (rawSeries = []) => {
    const series = normalizeGameScoreSeries(rawSeries);

    if (!series.length) {
        return null;
    }

    return series.reduce((best, point) => {
        if (isLowerGameScoreBetter(point.metric)) {
            return point.value < best.value ? point : best;
        }

        return point.value > best.value ? point : best;
    }, series[0]);
};

const getComparablePointValue = (point) => {
    const maxValue = Number(point?.maxValue);

    return Number.isFinite(maxValue) && maxValue > 0
        ? Number(point.value) / maxValue
        : Number(point?.value);
};

const getRelativeProgressIndex = (point, baseline) => {
    const currentValue = getComparablePointValue(point);
    const baselineValue = getComparablePointValue(baseline);

    if (!Number.isFinite(currentValue) || !Number.isFinite(baselineValue)) {
        return 100;
    }

    if (baselineValue === 0) {
        if (currentValue === 0) {
            return 100;
        }

        return currentValue > 0 ? 200 : 0;
    }

    const ratio = isLowerGameScoreBetter(point.metric)
        ? baselineValue / Math.max(Number.EPSILON, currentValue)
        : currentValue / Math.abs(baselineValue);

    return clamp(Math.round(ratio * 100), 0, 300);
};

const compareProgressEvents = (left, right) => {
    if (left.recordedAt && right.recordedAt && left.recordedAt !== right.recordedAt) {
        return left.recordedAt.localeCompare(right.recordedAt);
    }

    if (left.recordedAt && !right.recordedAt) {
        return 1;
    }

    if (!left.recordedAt && right.recordedAt) {
        return -1;
    }

    return left.order - right.order;
};

const buildOverallProgressSeries = (rawHistory = {}) => {
    const history = createGameScoreHistory(rawHistory);
    const events = [];
    let order = 0;

    Object.entries(history).forEach(([gameId, series]) => {
        const baseline = series[0];

        series.forEach((point) => {
            events.push({
                gameId,
                index: getRelativeProgressIndex(point, baseline),
                recordedAt: point.recordedAt,
                order,
            });
            order += 1;
        });
    });

    events.sort(compareProgressEvents);

    const latestIndexByGame = new Map();

    return events.map((event) => {
        latestIndexByGame.set(event.gameId, event.index);
        const values = [...latestIndexByGame.values()];
        const score = Math.round(values.reduce((total, value) => total + value, 0) / values.length);

        return {
            score,
            recordedAt: event.recordedAt,
            trackedGames: values.length,
        };
    }).slice(-GAME_SCORE_HISTORY_LIMIT);
};

const getGameScoreTrend = (rawSeries = []) => {
    const series = normalizeGameScoreSeries(rawSeries);

    if (series.length < 2) {
        return { changePercent: 0, direction: 'flat' };
    }

    const first = series[0];
    const latest = series[series.length - 1];
    const index = getRelativeProgressIndex(latest, first);
    const changePercent = index - 100;

    return {
        changePercent,
        direction: changePercent > 0 ? 'up' : changePercent < 0 ? 'down' : 'flat',
    };
};

module.exports = {
    GAME_SCORE_HISTORY_LIMIT,
    SOPT_PROGRESS_GAME_IDS,
    buildOverallProgressSeries,
    createGameScoreHistory,
    getBestGameScorePoint,
    getGameScoreTrend,
    isLowerGameScoreBetter,
    normalizeGameScorePoint,
    recordGameScore,
    seedGameScoreHistory,
};
